<script setup lang="ts">
import CustomButton from '@/components/CustomButton.vue';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
import Dialog from '@/components/dialogs/Dialog.vue';
import EntityChooseSelect from '@/components/dialogs/EntityChooseSelect.vue';
import DateField from '@/components/forms/DateField.vue';
import { useActionPointStore } from '@/stores/actionPoint';
import { useAuthStore } from '@/stores/auth';
import { useModuleStore } from '@/stores/module';
import { usePriorityStore } from '@/stores/priority';
import type { ActionPoint, DetailedActionPoint } from '@/types/actionPoint';
import { actionPointStatusTypeItems } from '@/types/actionPoint';
import type { Module } from '@/types/module';
import type { NameIdPair } from '@/types/nameIdPair';
import type { Priority } from '@/types/priority';
import type { UpdateActionPointsRequest } from '@/types/requests/updateActionPointsRequest';
import type { RiskNameIdPair } from '@/types/risk';
import { detailedActionPointToActionPoint } from '@/utils/actionPoint';
import { maximumDate, minimumDate } from '@/utils/date';
import { dateRange, required } from '@/utils/validation';
import { storeToRefs } from 'pinia';
import { computed, defineEmits, defineModel, defineProps, onMounted, ref, useTemplateRef, watch } from 'vue';

const actionPointStore = useActionPointStore();
const priorityStore = usePriorityStore();
const moduleStore = useModuleStore();
const authStore = useAuthStore();

const emit = defineEmits(['updateActionPoint', 'bulkUpdated']);

const props = defineProps<{
    selectedActionPoint: DetailedActionPoint | null;
    bulkEditIds?: string[];
    moduleNameIdPairs: NameIdPair[];
    riskNameIdPairs: RiskNameIdPair[];
    teamNameIdPairs: NameIdPair[];
    employeeNameIdPairs: NameIdPair[];
    sourceNameIdPairs: NameIdPair[];
    userIsActionCoordinator: boolean;
}>();

const form = useTemplateRef('form');
const priorities = ref<Priority[]>();

const responsibleType = ref<'Employee' | 'Team'>();
const executorType = ref<'Employee' | 'Team'>();

const isLoading = ref(false);
const actionPoint = ref<DetailedActionPoint>({} as DetailedActionPoint);
const isConfirmBulkEditDialogOpen = ref(false);

type BulkEditField = 'risk' | 'priority' | 'deadline' | 'responsible' | 'executor' | 'deadline' | 'source' | 'status';
const bulkEditFieldsVisible = ref<BulkEditField[]>([]);

const isDialogOpen = defineModel<boolean>();
const dialogTitle = computed(
    () =>
        `${props.bulkEditIds && props.bulkEditIds.length > 0 ? 'Actiepunten' : 'Actiepunt'} bewerken ${
            props.selectedActionPoint ? ` | ${props.selectedActionPoint.action}` : ''
        }`
);

const bulkEditText = computed(() => `${props.bulkEditIds?.length} ${props.bulkEditIds?.length === 1 ? 'actiepunt' : 'actiepunten'}`);

const disableInputForActionCoordinator = computed(
    () => props.userIsActionCoordinator && actionPoint.value.createdBy?._id !== authStore.loggedInEmployee?._id
);

const { isActionPointExecutorFeatureEnabled } = storeToRefs(authStore);

onMounted(async () => {
    priorities.value = await priorityStore.get();

    if (props.moduleNameIdPairs.length == 1) {
        actionPoint.value.module = props.moduleNameIdPairs[0] as Module;
    }

    setCurrentAssignType();
});

function setCurrentAssignType() {
    responsibleType.value = actionPoint.value.responsible?.type;
    executorType.value = actionPoint.value.executor?.type;
}

watch(
    [isDialogOpen, () => props.selectedActionPoint],
    ([isOpen, selectedActionPoint]) => {
        if (!isOpen) return;
        if (!selectedActionPoint) return;

        actionPoint.value = { ...selectedActionPoint };
        setCurrentAssignType();
    },
    { immediate: true }
);

async function updateActionPoint() {
    const formValidation = await form.value?.validate();
    if (!formValidation?.valid) return;

    if (!props.selectedActionPoint && props.bulkEditIds && props.bulkEditIds.length > 0) {
        isConfirmBulkEditDialogOpen.value = true;
        return;
    }

    isLoading.value = true;

    try {
        const updatedActionPoint = detailedActionPointToActionPoint(actionPoint.value);

        const result = await actionPointStore.update(updatedActionPoint);
        if (result.status !== 200) return;

        const detailedActionPoint = await actionPointStore.getDetailsById(updatedActionPoint._id);

        emit('updateActionPoint', detailedActionPoint);
        closeDialog();
    } finally {
        isLoading.value = false;
    }
}

function closeDialog() {
    form.value?.reset();
    delete actionPoint.value?.deadline;
    isDialogOpen.value = false;

    bulkEditFieldsVisible.value = [];
}

async function handleBulkEditConfirmed() {
    isConfirmBulkEditDialogOpen.value = false;
    if (!props.bulkEditIds) return;

    const updates: Partial<ActionPoint> = detailedActionPointToActionPoint(actionPoint.value);
    const request: UpdateActionPointsRequest = { ids: props.bulkEditIds, updates };

    try {
        await actionPointStore.updateByIds(request);
        emit('bulkUpdated');
    } finally {
        closeDialog();
    }
}

function toggleBulkEditField(field: BulkEditField) {
    if (bulkEditFieldsVisible.value.includes(field)) {
        bulkEditFieldsVisible.value = bulkEditFieldsVisible.value.filter((f) => f !== field);
    } else {
        bulkEditFieldsVisible.value.push(field);
    }
}
</script>

<template>
    <Dialog v-model="isDialogOpen" :title="dialogTitle" :isLoading="isLoading" persistent>
        <div v-if="!selectedActionPoint">
            Gebruik de vinkjes om de velden te kiezen die u wilt wijzigen voor de geselecteerde actiepunten. Niet aangevinkt betekent dat de bestaande
            waarde ongewijzigd blijft
        </div>
        <v-form ref="form" lazy-validation>
            <v-text-field v-if="selectedActionPoint" v-model="actionPoint.action" label="Actie" type="text" :rules="[required]" required />
            <v-textarea v-if="selectedActionPoint" v-model="actionPoint.description" label="Beschrijving" type="text" />

            <v-select
                v-if="moduleStore.areMultipleModulesAccessible && selectedActionPoint"
                v-model="actionPoint.module"
                label="Module"
                required
                :items="props.moduleNameIdPairs"
                item-title="name"
                return-object
                :rules="[required]"
            />

            <v-checkbox
                v-if="!selectedActionPoint"
                label="Wijzig risiconiveau"
                density="comfortable"
                @click="toggleBulkEditField('risk')"
                hide-details
            />
            <v-autocomplete
                v-if="selectedActionPoint || bulkEditFieldsVisible.includes('risk')"
                v-model="actionPoint.risk"
                label="Risiconiveau"
                :items="props.riskNameIdPairs"
                item-title="description"
                return-object
                :required="selectedActionPoint"
                :rules="selectedActionPoint ? [required] : []"
                clearable
                :disabled="disableInputForActionCoordinator"
            />

            <v-checkbox
                v-if="!selectedActionPoint"
                label="Wijzig prioriteit"
                density="comfortable"
                @click="toggleBulkEditField('priority')"
                hide-details
            />
            <v-autocomplete
                v-if="selectedActionPoint || bulkEditFieldsVisible.includes('priority')"
                v-model="actionPoint.priority"
                label="Prioriteit"
                :items="priorities"
                item-title="name"
                return-object
                clearable
            />

            <v-checkbox
                v-if="!selectedActionPoint"
                label="Wijzig deadline"
                density="comfortable"
                @click="toggleBulkEditField('deadline')"
                hide-details
            />
            <DateField
                v-if="selectedActionPoint || bulkEditFieldsVisible.includes('deadline')"
                v-model="actionPoint.deadline"
                label="Deadline"
                :include-time="true"
                :max="maximumDate"
                :min="minimumDate"
                :rules="[dateRange(minimumDate, maximumDate)]"
                :disabled="disableInputForActionCoordinator"
            />

            <v-checkbox
                v-if="!selectedActionPoint"
                label="Wijzig verantwoordelijke"
                density="comfortable"
                @click="toggleBulkEditField('responsible')"
                hide-details
            />
            <EntityChooseSelect
                v-if="selectedActionPoint || bulkEditFieldsVisible.includes('responsible')"
                v-model="actionPoint.responsible"
                v-model:type="responsibleType"
                :employees="props.employeeNameIdPairs"
                :teams="props.teamNameIdPairs"
                autocomplete-label="Verantwoordelijke"
                entityTypeLabel="Wat wil je toewijzen als verantwoordelijke?"
            />

            <v-checkbox
                v-if="!selectedActionPoint && isActionPointExecutorFeatureEnabled"
                label="Wijzig uitvoerder"
                density="comfortable"
                @click="toggleBulkEditField('executor')"
                hide-details
            />

            <EntityChooseSelect
                v-if="(selectedActionPoint || bulkEditFieldsVisible.includes('executor')) && isActionPointExecutorFeatureEnabled"
                v-model="actionPoint.executor"
                v-model:type="executorType"
                :employees="props.employeeNameIdPairs"
                :teams="props.teamNameIdPairs"
                autocomplete-label="Uitvoerder"
                entityTypeLabel="Wat wil je toewijzen als uitvoerder?"
            />

            <v-checkbox v-if="!selectedActionPoint" label="Wijzig bron" density="comfortable" @click="toggleBulkEditField('source')" hide-details />
            <v-autocomplete
                v-if="selectedActionPoint || bulkEditFieldsVisible.includes('source')"
                v-model="actionPoint.source"
                label="Bron"
                :items="sourceNameIdPairs"
                return-object
                item-title="name"
                clearable
            />

            <v-checkbox v-if="!selectedActionPoint" label="Wijzig status" density="comfortable" @click="toggleBulkEditField('status')" hide-details />
            <v-autocomplete
                v-if="bulkEditFieldsVisible.includes('status')"
                v-model="actionPoint.status"
                label="Status"
                :items="actionPointStatusTypeItems"
                item-title="title"
                item-value="value"
                :rules="[required]"
                required
                clearable
            />
        </v-form>

        <template #actions>
            <CustomButton class="mr-3" @click="updateActionPoint" :text="!bulkEditIds ? 'Wijzigen' : `${bulkEditText} wijzigen`" />
            <CustomButton color="red" @click="closeDialog" text="Annuleren" />
        </template>
    </Dialog>

    <ConfirmDialog
        @continue="handleBulkEditConfirmed"
        v-model="isConfirmBulkEditDialogOpen"
        :text="`Weet je zeker dat je ${bulkEditText} wil bewerken? De bestaande data wordt overschreven met de gemaakte aanpassingen!`"
    />
</template>
